import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EasyCropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import ProfileCardCachedImage from '../components/common/ProfileCardCachedImage';
import { PLATFORM_ADMIN_EMAIL } from '../constants/platformAdmin';
import { getSession } from '../services/authService';
import {
  addAdminProfileCardSlot,
  createAdminProfileCardSet,
  deleteAdminProfileCardSet,
  deleteAdminProfileCardSlot,
  getAdminProfileCardSets,
  renameAdminProfileCardSet,
  updateSlotRarity,
  uploadProfileCardSlotImage,
} from '../services/profileCardCatalogService';
import type {
  AdminProfileCardSetWithSlots,
  AdminProfileCardSlot,
  ProfileCardRarity,
} from '../types/profileCard';
import { profileCardSlotDisplayName, MAX_PROFILE_CARD_SLOTS_PER_SET } from '../types/profileCard';
import { cropSquareToWebp, readFileAsDataURL, type CropAreaPixels } from '../utils/imageCompress';
import {
  computeSlotDropPercents,
  profileCardExchangeCoinsForRarity,
  validateProfileCardSetRarities,
} from '../utils/profileCardRarity';
import {
  invalidateProfileCardImageCache,
  putProfileCardImageCacheFromBlob,
} from '../utils/profileCardImageCache';
import { readClipboardImageBlob } from '../utils/slideImageCompress';
import { supabase } from '../supabaseClient';
import ProfileCardRarityRulesGuide from '../components/pointItem/ProfileCardRarityRulesGuide';
import ProfileCardRaritySelect from '../components/pointItem/ProfileCardRaritySelect';
import './AdminProfileCardSetsPage.css';

const TrashIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 7H20M9 7V4.5C9 3.67157 9.67157 3 10.5 3H13.5C14.3284 3 15 3.67157 15 4.5V7M18.5 7L17.7 18.6C17.6 20 16.4 21 15 21H9C7.6 21 6.4 20 6.3 18.6L5.5 7"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 11V16M14 11V16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const Cropper = EasyCropper as unknown as React.ComponentType<{
  image?: string;
  crop: { x: number; y: number };
  zoom: number;
  aspect: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropChange: (location: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  onCropComplete?: (croppedArea: CropAreaPixels, croppedAreaPixels: CropAreaPixels) => void;
}>;

function isAdminSessionEmail(email: string): boolean {
  return email.trim().toLowerCase() === PLATFORM_ADMIN_EMAIL;
}

type DraftSlot = {
  localId: string;
  serverId: string | null;
  slotIndex: number;
  name: string;
  storagePath: string | null;
  imageVersion: number;
  rarity: ProfileCardRarity | null;
  localBlob: Blob | null;
  previewUrl: string | null;
};

type DraftSet = {
  localId: string;
  serverId: string | null;
  name: string;
  sortOrder: number;
  slots: DraftSlot[];
  isNew: boolean;
};

type SlotUploadTarget = { setLocalId: string; slotLocalId: string; displayNumber: number };

const PREVIEW_BASE_PRICE_COINS = 50;

function newLocalId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function slotsToDraft(slots: AdminProfileCardSlot[]): DraftSlot[] {
  return slots
    .slice()
    .sort((a, b) => a.slotIndex - b.slotIndex || a.id.localeCompare(b.id))
    .map((s) => ({
      localId: s.id || newLocalId('slot'),
      serverId: s.id || null,
      slotIndex: s.slotIndex,
      name: s.name ?? '',
      storagePath: s.storagePath,
      imageVersion: s.imageVersion,
      rarity: s.rarity,
      localBlob: null,
      previewUrl: null,
    }));
}

function emptyDraftSlots(count = 10): DraftSlot[] {
  return Array.from({ length: count }, (_, i) => ({
    localId: newLocalId('slot'),
    serverId: null,
    slotIndex: i + 1,
    name: '',
    storagePath: null,
    imageVersion: 0,
    rarity: null,
    localBlob: null,
    previewUrl: null,
  }));
}

function setsToDraft(rows: AdminProfileCardSetWithSlots[]): DraftSet[] {
  return rows.map((s) => ({
    localId: s.id,
    serverId: s.id,
    name: s.name,
    sortOrder: s.sortOrder,
    slots: slotsToDraft(s.slots),
    isNew: false,
  }));
}

function serializeDraft(sets: DraftSet[]) {
  return sets.map((s) => ({
    localId: s.localId,
    serverId: s.serverId,
    name: s.name,
    sortOrder: s.sortOrder,
    isNew: s.isNew,
    slots: s.slots.map((sl) => ({
      localId: sl.localId,
      serverId: sl.serverId,
      slotIndex: sl.slotIndex,
      name: sl.name,
      storagePath: sl.storagePath,
      imageVersion: sl.imageVersion,
      rarity: sl.rarity,
      hasLocalBlob: Boolean(sl.localBlob),
    })),
  }));
}

function revokeSlotPreview(slot: DraftSlot) {
  if (slot.previewUrl) URL.revokeObjectURL(slot.previewUrl);
}

async function fetchAdminServerSlots(
  setId: string
): Promise<Array<{ id: string; slotIndex: number; storagePath: string | null }>> {
  const { data, error } = await supabase
    .from('admin_profile_card_slots')
    .select('id, slot_index, storage_path')
    .eq('set_id', setId)
    .order('slot_index', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: String(r.id ?? ''),
    slotIndex: Number(r.slot_index) || 0,
    storagePath: typeof r.storage_path === 'string' ? r.storage_path : null,
  }));
}

const SlotUploadModal: React.FC<{
  target: SlotUploadTarget | null;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void>;
}> = ({ target, onClose, onSave }) => {
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const cropPixelsRef = React.useRef<CropAreaPixels | null>(null);

  useEffect(() => {
    setRawImageSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSaving(false);
    setError('');
    setIsDragging(false);
    cropPixelsRef.current = null;
  }, [target]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setError('');
    setRawImageSrc(await readFileAsDataURL(file));
  }, []);

  useEffect(() => {
    if (!target || rawImageSrc) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const blob = readClipboardImageBlob(e.clipboardData);
      if (!blob) return;
      e.preventDefault();
      e.stopPropagation();
      const file =
        blob instanceof File
          ? blob
          : new File([blob], 'clipboard-image.png', { type: blob.type || 'image/png' });
      void handleFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [target, rawImageSrc, handleFile]);

  if (!target) return null;

  const handleSave = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || saving) return;
    setSaving(true);
    setError('');
    try {
      const blob = await cropSquareToWebp(rawImageSrc, cropPixelsRef.current, 256, 0.82);
      await onSave(blob);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '이미지 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="cp-pc-upload-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-pc-upload-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-pc-upload-header">
          <h3>{target.displayNumber}번 카드 이미지</h3>
          <button type="button" className="cp-pc-upload-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>
        {!rawImageSrc ? (
          <label
            className={`cp-pc-upload-dropzone${isDragging ? ' is-dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleFile(file);
            }}
          >
            이미지를 드래그·클릭하거나 Ctrl+V로 붙여넣기
            <span className="cp-pc-upload-dropzone-hint">정사각형으로 크롭됩니다 · 10MB 이하</span>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                e.currentTarget.value = '';
                if (file) void handleFile(file);
              }}
            />
          </label>
        ) : (
          <>
            <div className="cp-pc-upload-crop">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, pixels) => {
                  cropPixelsRef.current = pixels;
                }}
              />
            </div>
            <div className="cp-pc-upload-zoom">
              <span>확대</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>
          </>
        )}
        {error ? <p className="cp-pc-upload-error">{error}</p> : null}
        <div className="cp-pc-upload-footer">
          <button type="button" className="cp-pc-btn cp-pc-btn--secondary" onClick={onClose} disabled={saving}>
            취소
          </button>
          {rawImageSrc ? (
            <button
              type="button"
              className="cp-pc-btn cp-pc-btn--primary"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? '적용 중…' : '적용'}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

const SlotCard: React.FC<{
  slot: DraftSlot;
  displayNumber: number;
  percent: number;
  editingName: boolean;
  onUploadClick: () => void;
  onDeleteSlot: () => void;
  onRarityChange: (rarity: ProfileCardRarity) => void;
  onStartEditName: () => void;
  onNameChange: (name: string) => void;
  onEndEditName: () => void;
}> = ({
  slot,
  displayNumber,
  percent,
  editingName,
  onUploadClick,
  onDeleteSlot,
  onRarityChange,
  onStartEditName,
  onNameChange,
  onEndEditName,
}) => {
  const hasImage = Boolean(slot.previewUrl || slot.storagePath || slot.localBlob);
  const expectedExchangeCoins = profileCardExchangeCoinsForRarity(
    PREVIEW_BASE_PRICE_COINS,
    slot.rarity
  );
  const displayName = profileCardSlotDisplayName({ name: slot.name, slotIndex: displayNumber });
  return (
    <div className={`cp-pc-slot${hasImage ? ' has-image' : ''}${slot.rarity ? ` is-rarity-${slot.rarity}` : ''}`}>
      {editingName ? (
        <input
          type="text"
          className="cp-pc-slot-name-input"
          value={slot.name}
          placeholder={`${displayNumber}번 카드`}
          maxLength={40}
          autoFocus
          aria-label={`${displayNumber}번 카드 이름`}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={onEndEditName}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              onEndEditName();
            }
          }}
        />
      ) : (
        <button
          type="button"
          className="cp-pc-slot-index"
          onClick={onStartEditName}
          title="클릭하여 카드 이름 설정"
          aria-label={`${displayNumber}번 카드 이름 설정`}
        >
          <span className="cp-pc-slot-index-num">{displayNumber}</span>
          <span className="cp-pc-slot-index-name">{displayName}</span>
        </button>
      )}
      <button type="button" className="cp-pc-slot-media" onClick={onUploadClick}>
        {hasImage ? (
          <ProfileCardCachedImage
            source="admin"
            storagePath={slot.storagePath}
            imageVersion={slot.imageVersion}
            previewUrl={slot.previewUrl}
            alt={displayName}
          />
        ) : (
          <span className="cp-pc-slot-empty">+</span>
        )}
      </button>
      <div className="cp-pc-slot-weight">
        <span className="cp-pc-slot-weight-label">카드 등급</span>
        <ProfileCardRaritySelect
          value={slot.rarity}
          disabled={!hasImage}
          aria-label={`${displayNumber}번 카드 등급`}
          onChange={onRarityChange}
        />
      </div>
      <div className="cp-pc-slot-percent">
        {hasImage && slot.rarity ? `뽑힐 확률 ${percent.toFixed(1)}%` : '뽑힐 확률 -'}
      </div>
      <div className="cp-pc-slot-exchange">
        <div className="cp-pc-slot-exchange-coins">
          예상 환전 코인 {hasImage && slot.rarity ? expectedExchangeCoins : '-'}
        </div>
        <div className="cp-pc-slot-exchange-base">
          <span>예상 구입단가</span>
          <span>{PREVIEW_BASE_PRICE_COINS}코인 기준</span>
        </div>
      </div>
      <button
        type="button"
        className="cp-pc-slot-remove"
        onClick={onDeleteSlot}
        aria-label="카드 삭제"
        title="카드 삭제"
      >
        <TrashIcon />
      </button>
    </div>
  );
};

const AdminProfileCardSetsPage: React.FC = () => {
  const [sessionEmail, setSessionEmail] = useState('');
  const [draftSets, setDraftSets] = useState<DraftSet[]>([]);
  const [baselineJson, setBaselineJson] = useState('');
  const [initialSlotsBySet, setInitialSlotsBySet] = useState<
    Record<string, Array<{ id: string; slotIndex: number }>>
  >({});
  const [deletedServerSetIds, setDeletedServerSetIds] = useState<string[]>([]);
  const [activeSetId, setActiveSetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [uploadTarget, setUploadTarget] = useState<SlotUploadTarget | null>(null);
  const [editingNameSlotId, setEditingNameSlotId] = useState<string | null>(null);
  const [deleteSetConfirm, setDeleteSetConfirm] = useState<DraftSet | null>(null);
  const [addSetModal, setAddSetModal] = useState<{ name: string } | null>(null);
  const draftRef = useRef<DraftSet[]>([]);
  draftRef.current = draftSets;

  const isAdmin = useMemo(() => isAdminSessionEmail(sessionEmail), [sessionEmail]);

  useEffect(() => {
    let cancelled = false;
    void getSession()
      .then((s) => {
        if (!cancelled) setSessionEmail((s?.user?.email ?? '').trim().toLowerCase());
      })
      .catch(() => {
        if (!cancelled) setSessionEmail('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSets = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await getAdminProfileCardSets();
      const drafts = setsToDraft(rows);
      draftRef.current.forEach((s) => s.slots.forEach(revokeSlotPreview));
      setDraftSets(drafts);
      setBaselineJson(JSON.stringify(serializeDraft(drafts)));
      const initial: Record<string, Array<{ id: string; slotIndex: number }>> = {};
      drafts.forEach((s) => {
        if (s.serverId) {
          initial[s.serverId] = s.slots
            .filter((sl) => sl.serverId)
            .map((sl) => ({ id: sl.serverId as string, slotIndex: sl.slotIndex }));
        }
      });
      setInitialSlotsBySet(initial);
      setDeletedServerSetIds([]);
      setActiveSetId((prev) =>
        prev && drafts.some((r) => r.localId === prev) ? prev : drafts[0]?.localId ?? null
      );
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void loadSets();
  }, [isAdmin, loadSets]);

  useEffect(() => {
    return () => {
      draftRef.current.forEach((s) => s.slots.forEach(revokeSlotPreview));
    };
  }, []);

  const activeSet = useMemo(
    () => draftSets.find((s) => s.localId === activeSetId) ?? null,
    [draftSets, activeSetId]
  );

  const dirty = useMemo(() => {
    if (deletedServerSetIds.length > 0) return true;
    return JSON.stringify(serializeDraft(draftSets)) !== baselineJson;
  }, [draftSets, baselineJson, deletedServerSetIds]);

  const orderedSlots = useMemo(
    () =>
      (activeSet?.slots ?? [])
        .slice()
        .sort((a, b) => a.slotIndex - b.slotIndex || a.localId.localeCompare(b.localId)),
    [activeSet?.slots]
  );

  const slotPercents = useMemo(() => {
    const likes = orderedSlots.map((s) => ({
      rarity: s.rarity,
      hasImage: Boolean(s.previewUrl || s.storagePath || s.localBlob),
    }));
    return computeSlotDropPercents(likes);
  }, [orderedSlots]);

  const drawableCount = useMemo(
    () =>
      orderedSlots.filter((s) => Boolean(s.previewUrl || s.storagePath || s.localBlob) && s.rarity)
        .length,
    [orderedSlots]
  );

  const patchActiveSet = (updater: (set: DraftSet) => DraftSet) => {
    if (!activeSetId) return;
    setDraftSets((prev) => prev.map((s) => (s.localId === activeSetId ? updater(s) : s)));
  };

  const openAddSetModal = () => {
    if (saving) return;
    setAddSetModal({ name: `세트${draftSets.length + 1}` });
  };

  const handleConfirmAddSet = () => {
    if (!addSetModal) return;
    const name = addSetModal.name.trim();
    if (!name) return;
    const localId = newLocalId('set');
    const next: DraftSet = {
      localId,
      serverId: null,
      name,
      sortOrder: draftSets.length + 1,
      slots: emptyDraftSlots(10),
      isNew: true,
    };
    setDraftSets((prev) => [...prev, next]);
    setActiveSetId(localId);
    setAddSetModal(null);
  };

  const handleRenameSet = (name: string) => {
    patchActiveSet((s) => ({ ...s, name }));
  };

  const requestDeleteSet = (set: DraftSet) => {
    if (saving) return;
    setDeleteSetConfirm(set);
  };

  const handleConfirmDeleteSet = () => {
    const target = deleteSetConfirm;
    if (!target) return;
    target.slots.forEach(revokeSlotPreview);
    if (target.serverId) {
      setDeletedServerSetIds((prev) =>
        prev.includes(target.serverId as string) ? prev : [...prev, target.serverId as string]
      );
    }
    setDraftSets((prev) => {
      const next = prev.filter((s) => s.localId !== target.localId);
      setActiveSetId((cur) => (cur === target.localId ? next[0]?.localId ?? null : cur));
      return next;
    });
    setDeleteSetConfirm(null);
  };

  const handleRarityChange = (slotLocalId: string, rarity: ProfileCardRarity) => {
    patchActiveSet((s) => ({
      ...s,
      slots: s.slots.map((sl) => (sl.localId === slotLocalId ? { ...sl, rarity } : sl)),
    }));
  };

  const handleNameChange = (slotLocalId: string, name: string) => {
    patchActiveSet((s) => ({
      ...s,
      slots: s.slots.map((sl) => (sl.localId === slotLocalId ? { ...sl, name } : sl)),
    }));
  };

  const handleUploadApply = async (blob: Blob) => {
    if (!uploadTarget) return;
    const previewUrl = URL.createObjectURL(blob);
    patchActiveSet((s) => ({
      ...s,
      slots: s.slots.map((sl) => {
        if (sl.localId !== uploadTarget.slotLocalId) return sl;
        if (sl.previewUrl) revokeSlotPreview(sl);
        return {
          ...sl,
          localBlob: blob,
          previewUrl,
          rarity: sl.rarity ?? 'normal',
        };
      }),
    }));
  };

  const handleAddSlot = () => {
    if (!activeSet) return;
    if (activeSet.slots.length >= MAX_PROFILE_CARD_SLOTS_PER_SET) {
      setSaveError(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
      return;
    }
    const nextIndex = Math.max(0, ...activeSet.slots.map((s) => s.slotIndex)) + 1;
    const localId = newLocalId('slot');
    patchActiveSet((s) => ({
      ...s,
      slots: [
        ...s.slots,
        {
          localId,
          serverId: null,
          slotIndex: nextIndex,
          storagePath: null,
          imageVersion: 0,
          name: '',
          rarity: null,
          localBlob: null,
          previewUrl: null,
        },
      ],
    }));
    setUploadTarget({
      setLocalId: activeSet.localId,
      slotLocalId: localId,
      displayNumber: activeSet.slots.length + 1,
    });
  };

  const handleDeleteSlot = (slot: DraftSlot, displayNumber: number) => {
    if (!window.confirm(`${displayNumber}번 카드를 삭제할까요? (저장하기를 눌러야 반영됩니다)`)) return;
    revokeSlotPreview(slot);
    patchActiveSet((s) => ({ ...s, slots: s.slots.filter((sl) => sl.localId !== slot.localId) }));
  };

  const syncSetSlots = async (setId: string, draft: DraftSet) => {
    const initial = initialSlotsBySet[setId] ?? [];
    const kept = new Set(draft.slots.map((s) => s.serverId).filter(Boolean) as string[]);
    for (const base of initial) {
      if (!kept.has(base.id)) {
        if (base.id) {
          const path = `${setId}/${base.slotIndex}.webp`;
          await invalidateProfileCardImageCache('admin', path);
        }
        await deleteAdminProfileCardSlot(setId, base.slotIndex);
      }
    }

    let unused = (await fetchAdminServerSlots(setId)).sort((a, b) => a.slotIndex - b.slotIndex);

    for (const slot of draft.slots) {
      let server = slot.serverId ? unused.find((u) => u.id === slot.serverId) ?? null : null;
      if (server) {
        unused = unused.filter((u) => u.id !== server!.id);
      } else if (draft.isNew && unused.length > 0) {
        server = unused.shift() ?? null;
      }
      if (!server) {
        const added = await addAdminProfileCardSlot(setId);
        server = {
          id: added.id,
          slotIndex: added.slotIndex,
          storagePath: added.storagePath,
        };
      }

      if (slot.localBlob) {
        const uploaded = await uploadProfileCardSlotImage(setId, server.slotIndex, slot.localBlob);
        await putProfileCardImageCacheFromBlob(
          'admin',
          uploaded.storagePath,
          uploaded.imageVersion,
          slot.localBlob
        );
        await updateSlotRarity(server.id, slot.rarity ?? 'normal', slot.name);
      } else {
        const hasImage = Boolean(slot.storagePath || server.storagePath);
        await updateSlotRarity(server.id, hasImage ? slot.rarity : null, slot.name);
      }
    }

    for (const leftover of unused) {
      await deleteAdminProfileCardSlot(setId, leftover.slotIndex);
    }
  };

  const handleDiscard = () => {
    if (saving || !dirty) return;
    if (!window.confirm('저장하지 않은 변경을 모두 취소할까요?')) return;
    setSaveError('');
    void loadSets();
  };

  const handleIntegratedSave = async () => {
    if (saving || !dirty) return;
    for (const draft of draftSets) {
      const err = validateProfileCardSetRarities(
        draft.slots.map((s) => ({
          rarity: s.rarity,
          hasImage: Boolean(s.previewUrl || s.storagePath || s.localBlob),
        }))
      );
      if (err) {
        setSaveError(`「${draft.name || '세트'}」: ${err}`);
        setActiveSetId(draft.localId);
        return;
      }
    }
    setSaving(true);
    setSaveError('');
    try {
      for (const setId of deletedServerSetIds) {
        await deleteAdminProfileCardSet(setId);
      }

      for (const draft of draftSets) {
        let setId = draft.serverId;
        if (draft.isNew || !setId) {
          setId = await createAdminProfileCardSet(draft.name.trim() || '세트', draft.sortOrder);
        } else {
          const baseline = JSON.parse(baselineJson || '[]') as Array<{
            serverId: string | null;
            name: string;
          }>;
          const prev = baseline.find((b) => b.serverId === setId);
          if (!prev || prev.name !== draft.name.trim()) {
            await renameAdminProfileCardSet(setId, draft.name.trim() || '세트');
          }
        }
        await syncSetSlots(setId, { ...draft, isNew: draft.isNew || !draft.serverId });
      }

      await loadSets();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <section
        className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-landing-page cp-admin-profile-cards-page"
        aria-labelledby="cp-admin-profile-cards-title"
      >
        <div className="cp-page-title-row cp-admin-notices-page-title-row">
          <div className="cp-admin-legal-title-stack">
            <h2 id="cp-admin-profile-cards-title">프로필 카드 관리</h2>
          </div>
        </div>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자만 사용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-landing-page cp-admin-profile-cards-page"
      aria-labelledby="cp-admin-profile-cards-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-profile-cards-title">프로필 카드 관리</h2>
          <ProfileCardRarityRulesGuide />
        </div>
        {!loading && !loadError ? (
          <div className="cp-admin-landing-title-actions">
            <button
              type="button"
              className="cp-btn-secondary"
              onClick={handleDiscard}
              disabled={saving || !dirty}
            >
              변경 취소
            </button>
            <button
              type="button"
              className="cp-btn-save-all cp-admin-landing-top-save-btn"
              onClick={() => void handleIntegratedSave()}
              disabled={saving || !dirty}
              title={dirty ? '저장하기' : '저장할 변경사항 없음'}
            >
              {saving ? '저장 중…' : dirty ? '저장하기' : '변경사항 없음'}
            </button>
          </div>
        ) : null}
      </div>

      {loading ? (
        <p className="cp-dashboard-placeholder" role="status">
          불러오는 중…
        </p>
      ) : loadError ? (
        <p className="cp-dashboard-placeholder" role="alert">
          {loadError}
        </p>
      ) : (
        <>
          {dirty ? (
            <p className="cp-admin-landing-dirty-hint" role="status">
              저장되지 않은 변경이 있습니다. 「저장하기」를 눌러 반영하세요.
            </p>
          ) : null}
          <div className="cp-pc-set-tabs" role="tablist">
            {draftSets.map((s) => {
              const selected = activeSetId === s.localId;
              return (
                <div
                  key={s.localId}
                  role="tab"
                  tabIndex={0}
                  aria-selected={selected}
                  className={`cp-pc-set-tab${selected ? ' is-active' : ''}`}
                  onClick={() => setActiveSetId(s.localId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveSetId(s.localId);
                    }
                  }}
                >
                  <span className="cp-pc-set-tab-name">
                    {s.name}
                    {s.isNew ? ' *' : ''}
                  </span>
                  <button
                    type="button"
                    className="cp-pc-set-tab-trash"
                    aria-label={`${s.name} 세트 삭제`}
                    title="세트 삭제"
                    disabled={saving}
                    onClick={(e) => {
                      e.stopPropagation();
                      requestDeleteSet(s);
                    }}
                  >
                    <TrashIcon />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="cp-pc-set-tab-add"
              onClick={openAddSetModal}
              disabled={saving}
            >
              + 세트 추가
            </button>
          </div>

          {activeSet ? (
            <div className="cp-pc-set-panel">
              <div className="cp-pc-set-panel-head">
                <input
                  type="text"
                  className="cp-pc-set-name-input"
                  value={activeSet.name}
                  onChange={(e) => handleRenameSet(e.target.value)}
                  disabled={saving}
                />
                <span className="cp-pc-set-weight-total">
                  뽑기 가능 카드: {drawableCount > 0 ? `${drawableCount}장` : '이미지를 등록해 주세요'}
                </span>
              </div>
              <div className="cp-pc-slot-grid">
                {orderedSlots.map((slot, index) => {
                  const displayNumber = index + 1;
                  return (
                    <SlotCard
                      key={slot.localId}
                      slot={slot}
                      displayNumber={displayNumber}
                      percent={slotPercents[index] ?? 0}
                      editingName={editingNameSlotId === slot.localId}
                      onUploadClick={() =>
                        setUploadTarget({
                          setLocalId: activeSet.localId,
                          slotLocalId: slot.localId,
                          displayNumber,
                        })
                      }
                      onDeleteSlot={() => handleDeleteSlot(slot, displayNumber)}
                      onRarityChange={(r) => handleRarityChange(slot.localId, r)}
                      onStartEditName={() => setEditingNameSlotId(slot.localId)}
                      onNameChange={(name) => handleNameChange(slot.localId, name)}
                      onEndEditName={() => setEditingNameSlotId(null)}
                    />
                  );
                })}
                <button
                  type="button"
                  className="cp-pc-slot-add"
                  onClick={handleAddSlot}
                  disabled={saving || (activeSet?.slots.length ?? 0) >= MAX_PROFILE_CARD_SLOTS_PER_SET}
                  title={
                    (activeSet?.slots.length ?? 0) >= MAX_PROFILE_CARD_SLOTS_PER_SET
                      ? `세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개`
                      : undefined
                  }
                >
                  <span className="cp-pc-slot-add-plus" aria-hidden>
                    +
                  </span>
                  <span>카드 추가</span>
                </button>
              </div>
            </div>
          ) : (
            <p className="cp-dashboard-placeholder">먼저 세트를 추가해 주세요.</p>
          )}

          {saveError ? (
            <p className="cp-pc-admin-save-error" role="alert">
              {saveError}
            </p>
          ) : null}
        </>
      )}

      {!loading && !loadError ? (
        <button
          type="button"
          className="cp-fab-save"
          onClick={() => void handleIntegratedSave()}
          disabled={saving || !dirty}
          aria-label="저장하기"
          title={dirty ? '저장하기' : '저장할 변경사항 없음'}
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
            alt=""
            className="cp-fab-save-icon"
          />
        </button>
      ) : null}

      <SlotUploadModal
        target={uploadTarget}
        onClose={() => setUploadTarget(null)}
        onSave={handleUploadApply}
      />

      {addSetModal ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={() => setAddSetModal(null)}
        >
          <div
            className="cp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-pc-admin-add-set-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-pc-admin-add-set-title">세트 이름 설정</h3>
            <div className="cp-modal-body">
              <label className="cp-evaluation-plan-field">
                <span>세트 이름</span>
                <input
                  type="text"
                  value={addSetModal.name}
                  onChange={(e) => setAddSetModal({ name: e.target.value })}
                  autoFocus
                  placeholder="예: 동물"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleConfirmAddSet();
                    }
                  }}
                />
              </label>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setAddSetModal(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleConfirmAddSet}
                disabled={!addSetModal.name.trim()}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteSetConfirm ? (
        <div
          className="cp-pin-confirm-overlay"
          role="presentation"
          onClick={() => setDeleteSetConfirm(null)}
        >
          <div
            className="cp-pin-confirm cp-pin-confirm--warn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-pc-admin-delete-set-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-pin-confirm-warn-icon" aria-hidden>
              !
            </div>
            <p id="cp-pc-admin-delete-set-title" className="cp-pin-confirm-warn-title">
              세트 삭제
            </p>
            <p className="cp-pin-confirm-warn-desc">
              「{deleteSetConfirm.name}」 세트를 삭제할까요?
            </p>
            <div className="cp-pin-confirm-actions cp-pin-confirm-actions--cancel-wide">
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => setDeleteSetConfirm(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={handleConfirmDeleteSet}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AdminProfileCardSetsPage;
